import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CVProcessorModal } from './CVProcessor';

/**
 * Traitements CV en tâche de fond.
 *
 * Le traitement d'un CV dure des dizaines de secondes ; la modale peut être
 * réduite en pastille pour travailler ailleurs. Tant que la modale était
 * montée par la carte candidat ou par la fiche, « ailleurs » s'arrêtait à
 * l'écran courant : changer de vue démontait le composant et le traitement
 * était perdu, sans même un message.
 *
 * L'hôte ci-dessous vit à la racine de l'application, HORS du commutateur de
 * vues : une génération lancée depuis n'importe quel écran survit à la
 * navigation, et sa pastille reste visible jusqu'à la fin.
 *
 * Usage :
 *   const { lancerCv } = useTraitementsCv();
 *   lancerCv({ candidat, besoinId, existingGenCvs, onUpdate });
 */

const Ctx = createContext(null);

export function useTraitementsCv() {
  return useContext(Ctx) || { lancerCv: () => {}, enCours: 0 };
}

export function TraitementsCvProvider({ showNotification, children }) {
  const [jobs, setJobs] = useState([]);   // [{ key, props }]
  const seq = useRef(0);

  const fermer = useCallback((key) => {
    setJobs(prev => prev.filter(j => j.key !== key));
  }, []);

  const lancerCv = useCallback((props) => {
    const cand = props?.candidat;
    if (cand?.id == null) { showNotification?.('Fiche candidat introuvable — impossible de lancer le traitement.', 'error'); return; }
    setJobs(prev => {
      // Un seul traitement par candidat : relancer le même remet la modale au
      // premier plan au lieu d'empiler deux générations concurrentes qui se
      // remplaceraient l'une l'autre sur la fiche.
      const dejaLa = prev.find(j => String(j.props?.candidat?.id) === String(cand.id)
        && String(j.props?.besoinId ?? '') === String(props?.besoinId ?? ''));
      if (dejaLa) {
        showNotification?.('Un traitement est déjà en cours pour ce candidat.', 'info');
        return prev;
      }
      seq.current += 1;
      return [...prev, { key: `cv-${seq.current}`, props }];
    });
  }, [showNotification]);

  return (
    <Ctx.Provider value={{ lancerCv, enCours: jobs.length }}>
      {children}
      {jobs.map(j => (
        <CVProcessorModal key={j.key} {...j.props}
          showNotification={showNotification}
          onClose={() => fermer(j.key)} />
      ))}
    </Ctx.Provider>
  );
}

export default TraitementsCvProvider;
